"use client";

import PageHero from "@/components/shared/PageHero";

import Typography from "@/components/ui/Typography";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { payPalIcon } from "@/public/assets/icons";
import { paypalClientId, paypalPlanId } from "@/environment";
import {
  ArrowPathIcon,
  CheckCircleIcon,
  CreditCardIcon,
  CurrencyDollarIcon,
  HeartIcon,
  LockClosedIcon,
  ServerStackIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UserGroupIcon,
  XMarkIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/outline";
import { HeartIcon as HeartSolid } from "@heroicons/react/24/solid";
import Image from "next/image";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { showToast } from "@/lib/toast";

const ONE_TIME_AMOUNTS = [5, 15, 25, 50];
const RECURRING_AMOUNTS = [3, 10, 20, 50];

/* ── Impact Card ── */
function ImpactCard({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="flex items-start gap-4">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue/10 text-blue">
        {icon}
      </div>
      <div>
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          {title}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>
      </div>
    </div>
  );
}

/* ── Checkout step type ── */
type CheckoutStep = "select" | "checkout" | "success";

/* ── Format number with commas ── */
const formatWithCommas = (value: string): string => {
  const num = value.replace(/[^\d.]/g, "");
  const [integer, decimal] = num.split(".");
  const formatted = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decimal !== undefined ? `${formatted}.${decimal}` : formatted;
};

const stripCommas = (value: string): string => value.replace(/,/g, "");

/* ════════════════════════════════════════════════════════════════
   SUPPORT US PAGE
   ════════════════════════════════════════════════════════════ */
export default function SupportDevelopers() {
  const { t } = useTranslation("supportUs");
  const [tab, setTab] = useState<"one-time" | "recurring">("one-time");
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState("");
  const [step, setStep] = useState<CheckoutStep>("select");

  const amounts = tab === "one-time" ? ONE_TIME_AMOUNTS : RECURRING_AMOUNTS;
  const displayAmount =
    selectedAmount ?? (customAmount ? Number(stripCommas(customAmount)) : 0);

  const handleContributeClick = () => {
    if (displayAmount <= 0) return;
    setStep("checkout");
  };

  const handleBack = () => {
    setStep("select");
  };

  const handlePayPalApprove = useCallback(
    (_data: Record<string, unknown>, actions: any) => {
      return actions.order.capture().then(() => {
        setStep("success");
        showToast.success(t("paymentSuccess"));
      });
    },
    [t]
  );

  const handleCreateOrder = useCallback(
    (_data: Record<string, unknown>, actions: any) => {
      return actions.order.create({
        purchase_units: [
          {
            amount: {
              value: displayAmount.toFixed(2),
              currency_code: "USD",
            },
            description: `Dwell Finders ${tab === "recurring" ? "Monthly" : "One-Time"} Support`,
          },
        ],
      });
    },
    [displayAmount, tab]
  );

  const handleCreateSubscription = useCallback(
    (_data: Record<string, unknown>, actions: any) => {
      return actions.subscription.create({
        plan_id: paypalPlanId || "",
        quantity: 1,
        custom_id: `monthly_${displayAmount}`,
      });
    },
    [displayAmount]
  );

  const resetAll = () => {
    setStep("select");
    setSelectedAmount(null);
    setCustomAmount("");
    setTab("one-time");
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* ── Hero ── */}
      <PageHero
        badge={t("heroBadge")}
        title={t("heroTitle")}
        subtitle={t("heroSubtitle")}
        topIcon={<HeartSolid className="h-8 w-8 text-red-400" />}
      />

      {/* ── Main Content: Donation + Sidebar ── */}
      <section className="mx-auto max-w-6xl px-4 py-20">
        <div className="grid gap-12 lg:grid-cols-5">
          {/* Donation Card — 3 cols */}
          <div className="lg:col-span-3">
            <Card className="border-0 bg-white dark:bg-gray-800 shadow-lg overflow-hidden">
              {/* Tab switcher */}
              <div className="flex border-b border-gray-100 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => {
                    setTab("one-time");
                    setSelectedAmount(null);
                    setCustomAmount("");
                  }}
                  className={`flex-1 flex items-center justify-center gap-2 py-4 text-sm font-semibold transition-colors ${
                    tab === "one-time"
                      ? "border-b-2 border-blue text-blue"
                      : "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                  }`}
                >
                  <CurrencyDollarIcon className="h-4 w-4" />
                  {t("oneTime")}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setTab("recurring");
                    setSelectedAmount(null);
                    setCustomAmount("");
                  }}
                  className={`flex-1 flex items-center justify-center gap-2 py-4 text-sm font-semibold transition-colors ${
                    tab === "recurring"
                      ? "border-b-2 border-blue text-blue"
                      : "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                  }`}
                >
                  <ArrowPathIcon className="h-4 w-4" />
                  {t("recurring")}
                </button>
              </div>

              <CardContent className="p-8 sm:p-10">
                <div className="mb-2">
                  <Typography variant="h4" className="mb-1">
                    {tab === "one-time" ? t("oneTimeTitle") : t("recurringTitle")}
                  </Typography>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {tab === "one-time" ? t("oneTimeDesc") : t("recurringDesc")}
                  </p>
                </div>

                {/* Amount grid */}
                <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {amounts.map((amount) => (
                    <button
                      key={amount}
                      type="button"
                      onClick={() => {
                        setSelectedAmount(amount);
                        setCustomAmount("");
                      }}
                      className={`flex flex-col items-center rounded-xl border-2 px-4 py-4 text-center transition-all ${
                        selectedAmount === amount
                          ? "border-blue bg-blue/5 text-blue shadow-md shadow-blue/10"
                          : "border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-blue/40"
                      }`}
                    >
                      <span className="text-xl font-bold">${amount}</span>
                      {tab === "recurring" && (
                        <span className="text-xs text-gray-400 mt-0.5">
                          {t("perMonth")}
                        </span>
                      )}
                    </button>
                  ))}
                </div>

                {/* Custom amount */}
                <div className="mt-5">
                  <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t("customLabel")}
                  </label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-400">
                      $
                    </span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={customAmount}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/[^\d.]/g, "");
                        setCustomAmount(formatWithCommas(raw));
                        setSelectedAmount(null);
                      }}
                      placeholder={t("customPlaceholder")}
                      className="h-11 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 pl-8 pr-14 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 outline-none transition-colors focus:ring-1 focus:ring-blue focus:border-blue"
                    />
                    <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-400">
                      $
                    </span>
                  </div>
                </div>

                {/* Summary + CTA / Checkout / Success */}
                <div className="mt-8 rounded-xl bg-gray-50 dark:bg-gray-700/50 p-5">
                  {/* ── Step 1: Select amount ── */}
                  {step === "select" && (
                    <>
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {t("contributionAmount")}
                        </span>
                        <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
                          ${displayAmount ? displayAmount.toLocaleString() : "0"}
                          {tab === "recurring" && displayAmount > 0 && (
                            <span className="text-xs font-normal text-gray-400">
                              /{t("mo")}
                            </span>
                          )}
                        </span>
                      </div>
                      <Button
                        onClick={handleContributeClick}
                        disabled={displayAmount <= 0}
                        className="w-full h-12 bg-blue text-white hover:bg-blue/90 text-base font-semibold disabled:opacity-40"
                      >
                        <HeartIcon className="mr-2 h-5 w-5" />
                        {tab === "one-time"
                          ? t("contributeNow")
                          : t("startMonthly")}
                      </Button>
                    </>
                  )}

                  {/* ── Step 2: PayPal checkout ── */}
                  {step === "checkout" && (
                    <div className="space-y-5">
                      {/* Back button + order summary */}
                      <div className="flex items-center justify-between">
                        <button
                          type="button"
                          onClick={handleBack}
                          className="flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                        >
                          <ArrowLeftIcon className="h-4 w-4" />
                          {t("back")}
                        </button>
                        <div className="text-right">
                          <p className="text-xs text-gray-400">
                            {t("contributionAmount")}
                          </p>
                          <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                            ${Number(displayAmount.toFixed(2)).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            {tab === "recurring" && (
                              <span className="text-xs font-normal text-gray-400">
                                /{t("mo")}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>

                      <div className="h-px bg-gray-200 dark:bg-gray-600" />

                      {/* Payment method label */}
                      <div className="flex items-center gap-2">
                        <LockClosedIcon className="h-4 w-4 text-green-500" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                          {t("secureCheckout")}
                        </span>
                      </div>

                      {/* PayPal Buttons */}
                      <PayPalScriptProvider
                        options={{
                          clientId: paypalClientId || "test",
                          currency: "USD",
                          intent:
                            tab === "recurring" ? "subscription" : "capture",
                          vault: tab === "recurring" ? true : undefined,
                        }}
                      >
                        <PayPalButtons
                          style={{
                            layout: "vertical",
                            color: "blue",
                            shape: "rect",
                            label: "paypal",
                            height: 48,
                          }}
                          createOrder={
                            tab === "one-time"
                              ? handleCreateOrder
                              : undefined
                          }
                          createSubscription={
                            tab === "recurring"
                              ? handleCreateSubscription
                              : undefined
                          }
                          onApprove={handlePayPalApprove}
                          onError={() => {
                            showToast.error(t("paymentError"));
                          }}
                          onCancel={() => {
                            showToast.info(t("paymentCancelled"));
                            setStep("select");
                          }}
                        />
                      </PayPalScriptProvider>

                      <p className="text-center text-xs text-gray-400 dark:text-gray-500">
                        {t("checkoutSecureNote")}
                      </p>
                    </div>
                  )}

                  {/* ── Step 3: Success ── */}
                  {step === "success" && (
                    <div className="flex flex-col items-center py-4 text-center">
                      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                        <CheckCircleIcon className="h-8 w-8 text-green-500" />
                      </div>
                      <Typography variant="h4" className="mb-2">
                        {t("thankYouTitle")}
                      </Typography>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                        {t("thankYouMessage")}
                      </p>
                      <p className="text-lg font-bold text-blue mb-4">
                        ${Number(displayAmount.toFixed(2)).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        {tab === "recurring" && (
                          <span className="text-xs font-normal text-gray-400">
                            /{t("mo")}
                          </span>
                        )}
                      </p>
                      <Button
                        onClick={resetAll}
                        variant="outline"
                        className="mt-2"
                      >
                        {t("makeAnother")}
                      </Button>
                    </div>
                  )}
                </div>

                {/* Payment methods strip (only on select step) */}
                {step === "select" && (
                  <div className="mt-6 flex items-center justify-center gap-6">
                    <div className="flex items-center gap-2 text-gray-400">
                      <Image
                        src={payPalIcon.src}
                        width={20}
                        height={20}
                        alt="PayPal"
                      />
                      <span className="text-xs">{t("paypal")}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-400">
                      <CreditCardIcon className="h-5 w-5" />
                      <span className="text-xs">{t("creditDebit")}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-400">
                      <LockClosedIcon className="h-4 w-4" />
                      <span className="text-xs">{t("sslSecure")}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar — 2 cols */}
          <div className="space-y-8 lg:col-span-2">
            {/* Where your money goes */}
            <Card className="border-0 bg-white dark:bg-gray-800 shadow-lg">
              <CardContent className="p-8">
                <Typography variant="h5" className="mb-6">
                  {t("impactTitle")}
                </Typography>
                <div className="space-y-5">
                  <ImpactCard
                    icon={<ServerStackIcon className="h-5 w-5" />}
                    title={t("impact1Title")}
                    description={t("impact1Desc")}
                  />
                  <ImpactCard
                    icon={<SparklesIcon className="h-5 w-5" />}
                    title={t("impact2Title")}
                    description={t("impact2Desc")}
                  />
                  <ImpactCard
                    icon={<UserGroupIcon className="h-5 w-5" />}
                    title={t("impact3Title")}
                    description={t("impact3Desc")}
                  />
                  <ImpactCard
                    icon={<ShieldCheckIcon className="h-5 w-5" />}
                    title={t("impact4Title")}
                    description={t("impact4Desc")}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Dev team message */}
            <div className="rounded-xl bg-gradient-to-br from-blue to-blue-dark p-6 text-white">
              <Typography variant="h5" className="mb-3 !text-white">
                {t("devTeamTitle")}
              </Typography>
              <p className="text-sm leading-relaxed text-blue-100/80">
                {t("devTeamMessage")}
              </p>
              <div className="mt-4 flex items-center gap-2 text-xs text-blue-100/60">
                <CheckCircleIcon className="h-4 w-4" />
                {t("transparency")}
              </div>
            </div>

            {/* Trust badges */}
            <Card className="border-0 bg-white dark:bg-gray-800 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center justify-center gap-6">
                  <div className="flex flex-col items-center gap-1 text-center">
                    <LockClosedIcon className="h-6 w-6 text-green-500" />
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {t("sslEncrypted")}
                    </span>
                  </div>
                  <div className="h-8 w-px bg-gray-200 dark:bg-gray-700" />
                  <div className="flex flex-col items-center gap-1 text-center">
                    <ShieldCheckIcon className="h-6 w-6 text-blue" />
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {t("pciCompliant")}
                    </span>
                  </div>
                  <div className="h-8 w-px bg-gray-200 dark:bg-gray-700" />
                  <div className="flex flex-col items-center gap-1 text-center">
                    <CreditCardIcon className="h-6 w-6 text-purple-500" />
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {t("securePayments")}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ── Disclaimer ── */}
      <section className="border-t border-gray-200 dark:border-gray-800">
        <div className="mx-auto max-w-4xl px-4 py-8 text-center">
          <p className="text-xs leading-relaxed text-gray-400 dark:text-gray-500">
            {t("disclaimer")}
          </p>
        </div>
      </section>
    </div>
  );
}
